import React, { useState, useCallback, memo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiMoreVertical,
  FiEdit,
  FiTrash2,
  FiCheckCircle,
  FiXCircle,
  FiEye,
} from "react-icons/fi";
import { toast } from "react-toastify";
import axios from "axios";

const api = axios.create({ baseURL: "https://algoritm-api-edu.uz" });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const MenuItem = memo(({ icon: Icon, text, color, hoverBg, onClick }) => (
  <motion.button
    whileHover={{ backgroundColor: hoverBg, x: 6 }}
    whileTap={{ scale: 0.97 }}
    onClick={onClick}
    style={{
      display: "flex",
      alignItems: "center",
      gap: "10px",
      width: "100%",
      padding: "12px 18px",
      background: "white",
      border: "none",
      textAlign: "left",
      fontSize: "14.5px",
      fontWeight: "500",
      cursor: "pointer",
    }}
  >
    <Icon size={18} color={color} />
    <span>{text}</span>
  </motion.button>
));

const EventActionsDropdown = memo(({ event, onEdit, refreshList, onView }) => {
  const [open, setOpen] = useState(false);

  const toggle = useCallback((e) => {
    e.stopPropagation();
    setOpen((v) => !v);
  }, []);

  const closeOnOutside = useCallback(
    (e) => {
      if (open) {
        e.stopPropagation();
        setOpen(false);
      }
    },
    [open]
  );

  const handleConfirm = useCallback(async () => {
    if (!window.confirm("Tadbir tasdiqlansinmi?")) return;
    try {
      await api.put(`/api/admin/Event/Confirm/${event?.id}`);
      toast.success("Tasdiqlandi");
      refreshList();
    } catch {
      toast.error("Xato");
    } finally {
      setOpen(false);
    }
  }, [event?.id, refreshList]);

  const handleCancel = useCallback(async () => {
    if (!window.confirm("Bekor qilinsinmi?")) return;
    try {
      await api.put(`/api/admin/Event/Cancel/${event?.id}`);
      toast.success("Bekor qilindi");
      refreshList();
    } catch {
      toast.error("Xato");
    } finally {
      setOpen(false);
    }
  }, [event?.id, refreshList]);

  const handleDelete = useCallback(async () => {
    if (!window.confirm("O‘chirilsinmi?")) return;
    try {
      await api.delete(`/api/admin/Event/Delete/${event?.id}`);
      toast.success("O‘chirildi");
      refreshList();
    } catch {
      toast.error("Xato");
    } finally {
      setOpen(false);
    }
  }, [event?.id, refreshList]);

  const handleEditClick = useCallback(() => {
    onEdit();
    setOpen(false);
  }, [onEdit]);

  if (!event || !event.id) {
    return (
      <button
        disabled
        style={{ opacity: 0.4, cursor: "not-allowed", padding: "6px 8px" }}
      >
        <FiMoreVertical size={20} color="#ccc" />
      </button>
    );
  }

  const isConfirmed = event.canCancel === true;

  return (
    <div style={{ position: "relative" }}>
      <motion.button
        whileHover={{ scale: 1.12 }}
        whileTap={{ scale: 0.92 }}
        onClick={toggle}
        style={{
          background: "rgba(255,255,255,0.8)",
          border: "1px solid #e2e8f0",
          borderRadius: "8px",
          padding: "6px 8px",
          cursor: "pointer",
        }}
      >
        <FiMoreVertical size={20} color="#64748b" />
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: -8 }}
            transition={{
              duration: 0.18,
              type: "spring",
              stiffness: 500,
              damping: 30,
            }}
            style={{
              position: "absolute",
              top: "50%",
              right: "100%",
              transform: "translateY(-50%)",
              marginRight: "12px",
              background: "white",
              borderRadius: "12px",
              boxShadow: "0 12px 40px rgba(0,0,0,0.16)",
              zIndex: 9999,
              minWidth: "195px",
              border: "1px solid #e2e8f0",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <MenuItem
              icon={FiEye}
              text="Ko‘rish"
              color="#4f46e5"
              hoverBg="#eef2ff"
              onClick={() => {
                onView?.();
                setOpen(false);
              }}
            />
            {isConfirmed ? (
              <MenuItem
                icon={FiXCircle}
                text="Bekor qilish"
                color="#d97706"
                hoverBg="#fffbeb"
                onClick={handleCancel}
              />
            ) : (
              <>
                {event.canConfirm && (
                  <MenuItem
                    icon={FiCheckCircle}
                    text="Tasdiqlash"
                    color="#16a34a"
                    hoverBg="#f0fdf4"
                    onClick={handleConfirm}
                  />
                )}
                {event.canEdit && (
                  <MenuItem
                    icon={FiEdit}
                    text="Tahrirlash"
                    color="#2563eb"
                    hoverBg="#eff6ff"
                    onClick={handleEditClick}
                  />
                )}
                {event.canDelete && (
                  <>
                    <div style={{ height: "1px", background: "#e2e8f0" }} />
                    <MenuItem
                      icon={FiTrash2}
                      text="O‘chirish"
                      color="#dc2626"
                      hoverBg="#fee2e2"
                      onClick={handleDelete}
                    />
                  </>
                )}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {open && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9998,
            cursor: "default",
          }}
          onClick={closeOnOutside}
        />
      )}
    </div>
  );
});

EventActionsDropdown.displayName = "EventActionsDropdown";

export default EventActionsDropdown;
